import React, { useEffect, useRef, useState } from 'react';

export const useFadeIn = (options = {}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef();

  const {
    threshold = 0.1,
    rootMargin = '0px 0px -50px 0px',
    triggerOnce = true,
    delay = 0
  } = options;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && (!triggerOnce || !hasAnimated)) {
          if (delay > 0) {
            setTimeout(() => {
              setIsVisible(true);
              if (triggerOnce) setHasAnimated(true);
            }, delay);
          } else {
            setIsVisible(true);
            if (triggerOnce) setHasAnimated(true);
          }
        } else if (!triggerOnce && !entry.isIntersecting) {
          setIsVisible(false);
        }
      },
      {
        threshold,
        rootMargin
      }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [threshold, rootMargin, triggerOnce, delay, hasAnimated]);

  return [ref, isVisible];
};

export const useStaggeredFadeIn = (count, baseDelay = 0, staggerDelay = 200) => {
  const refs = useRef([]);
  const [visibleItems, setVisibleItems] = useState(new Set());

  // Initialize refs array
  useEffect(() => {
    refs.current = refs.current.slice(0, count);
    for (let i = refs.current.length; i < count; i++) {
      refs.current[i] = React.createRef();
    }
  }, [count]);

  useEffect(() => {
    const observers = refs.current.map((ref, index) => {
      return new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              setVisibleItems(prev => new Set([...prev, index]));
            }, baseDelay + (index * staggerDelay));
          }
        },
        {
          threshold: 0.1,
          rootMargin: '0px 0px -50px 0px'
        }
      );
    });

    refs.current.forEach((ref, index) => {
      if (ref.current) {
        observers[index].observe(ref.current);
      }
    });

    return () => {
      observers.forEach((observer, index) => {
        if (refs.current[index]?.current) {
          observer.unobserve(refs.current[index].current);
        }
      });
    };
  }, [count, baseDelay, staggerDelay]);

  return [refs.current, visibleItems];
};